import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectServicesVersions } from "../../features/Dashboard/Slices/dashboardSlice";
import { useAppDispatch } from "../../app/store";
import { getServicesVersions } from "../../features/Dashboard/AsyncThunks/servicios/getServicesStatus";
import RepoTimeline from "../../components/GitLabTimeline/RepoTimeline";
import useAuth from "../../hooks/useAuth";

type ServiceVersionItem = {
  name: string;
  version: string;
  environment: string;
  category: string;
  status: "loading" | "ok" | "error";
};

const CATEGORY_ORDER = [
  "Principal",
  "Múltiples Inscripciones",
  "Múltiples Validaciones",
  "Múltiples Cupos",
  "Múltiples Oferta",
];

// Devuelve el environment "global" del deploy: el más frecuente entre los
// servicios que ya respondieron (todos los workers de un mismo deploy
// devuelven el mismo environment, así que un solo badge arriba alcanza).
const pickGlobalEnvironment = (services: ServiceVersionItem[]) => {
  const counts: Record<string, number> = {};
  services.forEach((svc) => {
    if (svc.status === "ok" && svc.environment && svc.environment !== "-") {
      counts[svc.environment] = (counts[svc.environment] || 0) + 1;
    }
  });
  const entries = Object.entries(counts);
  if (!entries.length) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
};

const environmentBadgeClass = (env: string | null) => {
  const normalized = (env || "").toLowerCase();
  if (normalized.includes("prd") || normalized.includes("prod")) {
    return "bg-danger_light text-danger_dark border border-danger";
  }
  if (normalized.includes("staging")) {
    return "bg-warning_light text-warning_dark border border-warning";
  }
  if (normalized.includes("local") || normalized.includes("dev")) {
    return "bg-info_light text-info_dark border border-info";
  }
  return "bg-slate_100 text-slate_500 border border-slate_300";
};

const VersionBadge: React.FC<{ item: ServiceVersionItem }> = ({ item }) => {
  if (item.status === "loading") {
    return (
      <span className="block bg-slate_200 rounded-full h-5 w-16 animate-pulse" />
    );
  }

  const isError = item.status === "error" || item.version === "-";
  const pillClass = isError
    ? "bg-danger_light text-danger_dark border border-danger"
    : "bg-success_light text-success_dark border border-success";

  return (
    <span
      className={`block text-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none ${pillClass}`}
    >
      {item.version && item.version !== "-" ? `v${item.version}` : "-"}
    </span>
  );
};

const cardClasses = (status: ServiceVersionItem["status"], version: string) => {
  if (status === "loading") {
    return "border-l-slate_300 bg-white";
  }
  if (status === "error" || version === "-") {
    return "border-l-danger bg-danger_light/50";
  }
  return "border-l-success bg-white";
};

const ServiceCard: React.FC<{ item: ServiceVersionItem }> = ({ item }) => {
  return (
    <div
      className={`rounded-lg border border-cardBorder border-l-4 ${cardClasses(item.status, item.version)} px-2.5 py-1.5 flex items-center justify-between gap-2 hover:brightness-95 transition-all`}
    >
      <span className="text-[12px] font-medium text-textSecondary truncate min-w-0 flex-1">
        {item.name}
      </span>
      <span className="shrink-0 w-16">
        <VersionBadge item={item} />
      </span>
    </div>
  );
};

const CategoryStats: React.FC<{ items: ServiceVersionItem[] }> = ({ items }) => {
  const ok = items.filter((s) => s.status === "ok" && s.version !== "-").length;
  const errors = items.filter((s) => s.status === "error" || s.version === "-").length;
  const loading = items.filter((s) => s.status === "loading").length;
  return (
    <span className="inline-flex items-center gap-2 text-[11px] font-normal">
      {ok > 0 && <span className="text-success_dark">{ok} {ok > 1 ? "Operativos" : "Operativo"}</span>}
      {errors > 0 && (
        <span className="text-danger_dark">
          {errors} error{errors > 1 ? "es" : ""}
        </span>
      )}
      {loading > 0 && <span className="text-slate_400">{loading} cargando</span>}
    </span>
  );
};

function Servicios() {
  const servicesVersions = useSelector(
    selectServicesVersions
  ) as ServiceVersionItem[];
  const dispatch = useAppDispatch();
  const { hasPermission } = useAuth();

  const refresh = () => {
    dispatch(getServicesVersions());
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading = useMemo(
    () => servicesVersions.some((s) => s.status === "loading"),
    [servicesVersions]
  );

  const stats = useMemo(() => {
    const total = servicesVersions.length;
    const resolved = servicesVersions.filter((s) => s.status !== "loading").length;
    const ok = servicesVersions.filter((s) => s.status === "ok" && s.version !== "-").length;
    const errors = servicesVersions.filter((s) => s.status === "error" || s.version === "-").length;
    const loading = total - resolved;
    const progress = total > 0 ? Math.round((resolved / total) * 100) : 0;
    return { total, resolved, ok, errors, loading, progress };
  }, [servicesVersions]);

  const environment = useMemo(
    () => pickGlobalEnvironment(servicesVersions),
    [servicesVersions]
  );

  const groupedByCategory = useMemo(() => {
    return servicesVersions.reduce<Record<string, ServiceVersionItem[]>>(
      (acc, repo) => {
        const category = repo.category || "Sin categoría";
        if (!acc[category]) acc[category] = [];
        acc[category].push(repo);
        return acc;
      },
      {}
    );
  }, [servicesVersions]);

  const sortedCategories = useMemo(() => {
    return Object.keys(groupedByCategory).sort((a, b) => {
      const indexA = CATEGORY_ORDER.indexOf(a);
      const indexB = CATEGORY_ORDER.indexOf(b);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [groupedByCategory]);

  return (
    <div className="p-4 sm:p-6 w-full max-w-full flex flex-col gap-4 sm:gap-6">
      <div className="bg-white p-4 sm:p-6 rounded-lg w-full border-2 border-cardBorder overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-2 gap-y-3 mb-3">
          <h4 className="font-semibold text-textSecondary text-[16px]">
            Versiones de servicios
          </h4>
          {environment && (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${environmentBadgeClass(
                environment
              )}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
              {environment}
            </span>
          )}
          <div className="ml-auto">
            <button
              className="text-textPrimary font-bold hover:underline border-2 border-cardBorder px-3 py-1 rounded-lg hover:bg-cardBorder disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={refresh}
              disabled={
                !hasPermission("servicios", "actualizar_estado_servicios") ||
                isLoading
              }
            >
              Actualizar
            </button>
          </div>
        </div>

        {/* Stats + barra de progreso */}
        {stats.total > 0 && (
          <div className="mb-4 space-y-2">
            <div className="flex flex-wrap items-center gap-3 text-[12px]">
              <span className="text-textSecondary">{stats.total} servicios</span>
              {stats.ok > 0 && (
                <span className="inline-flex items-center gap-1 text-success_dark font-medium">
                  <span className="w-2 h-2 rounded-full bg-success" />
                  {stats.ok > 1 ? "Operativos" : "Operativo"}
                </span>
              )}
              {stats.errors > 0 && (
                <span className="inline-flex items-center gap-1 text-danger_dark font-medium">
                  <span className="w-2 h-2 rounded-full bg-danger" />
                  {stats.errors} error{stats.errors > 1 ? "es" : ""}
                </span>
              )}
              {stats.loading > 0 && (
                <span className="inline-flex items-center gap-1 text-slate_500">
                  <span className="w-2 h-2 rounded-full bg-slate_300 animate-pulse" />
                  {stats.loading} consultando…
                </span>
              )}
            </div>
            {isLoading && (
              <div className="w-full h-1 bg-slate_100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out bg-primary"
                  style={{ width: `${stats.progress}%` }}
                />
              </div>
            )}
          </div>
        )}

        <div className="space-y-5">
          {sortedCategories.map((category) => (
            <div key={category} className="space-y-2">
              <div className="flex items-center justify-between border-b border-cardBorder pb-1">
                <h5 className="font-semibold text-textSecondary text-sm">
                  {category}
                </h5>
                <CategoryStats items={groupedByCategory[category]} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {groupedByCategory[category].map((repo) => (
                  <ServiceCard
                    key={`${repo.category}-${repo.name}`}
                    item={repo}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg w-full border-2 border-cardBorder overflow-x-scroll">
        <h4 className="font-semibold text-textSecondary mb-3 text-[16px]">
          Ambientes
        </h4>
        <div className="min-w-[920px] relative overflow-hidden">
          <div className="z-0 absolute flex h-screen font-bold">
            <div className="border-2 w-[300px] invisible"></div>
            <div className="border-l-2 border-[#ffffff] w-[150px] h-full bg-[#eef2ff]">
              <h2 className="text-center pt-4">DEVELOP</h2>
            </div>
            <div className="border-l-2 border-[#ffffff] w-[150px] h-full bg-[#e0e7ff]">
              <h2 className="text-center pt-4">TESTING</h2>
            </div>
            <div className="border-l-2 border-[#ffffff] w-[150px] h-full ">
              <h2 className="text-center pt-4">STAGING</h2>
            </div>
            <div className="border-l-2 border-[#ffffff] w-[150px] h-full bg-[#6366f1]">
              <h2 className="text-center pt-4 text-white">PRODUCTION</h2>
            </div>
          </div>

          <div className="z-10 mt-10 relative space-y-6 py-4">
            {/* Frontend */}
            <RepoTimeline projectId={406} />
            {/* Dashboard */}
            <RepoTimeline projectId={419} />
            {/* Notifications */}
            <RepoTimeline projectId={411} />
            {/* Api */}
            <RepoTimeline projectId={407} />
            {/* Sync */}
            <RepoTimeline projectId={410} />
            {/* Proxy */}
            <RepoTimeline projectId={404} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Servicios;
